// Partner attribution captured at signup.
//
// Two jobs. First, keep our own copy of the referral link slug and the
// tracking cookie, because the partner platform's attribution is invisible
// to us at purchase time. Second, resolve who the signup belongs to: the
// named referral list is checked before the link, so a partner who named
// the company by conversation is credited ahead of a later click.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { normalizePartnerEmail } from "@/lib/partner-fee-guard";

const input = z.object({
  userId: z.string().uuid().nullable().optional(),
  email: z.string().email().max(320),
  affiliateSlug: z.string().max(120).nullable().optional(),
  grId: z.string().max(200).nullable().optional(),
  landingUrl: z.string().max(500).nullable().optional(),
});

export const recordPartnerReferral = createServerFn({ method: "POST" })
  .inputValidator((data) => input.parse(data))
  .handler(async ({ data }) => {
    const { resolveSignupAttribution } = await import(
      "@/lib/partner-attribution.server"
    );
    let stored = false;
    try {
      if (data.affiliateSlug || data.grId) {
        await supabaseAdmin.from("partner_signup_attributions").upsert(
          {
            user_id: data.userId ?? null,
            email: data.email,
            email_normalized: normalizePartnerEmail(data.email),
            affiliate_slug: data.affiliateSlug ?? null,
            gr_id: data.grId ?? null,
            landing_url: data.landingUrl ?? null,
            source: "signup",
          },
          { onConflict: "user_id" },
        );
        stored = true;
      }
    } catch (e) {
      console.warn("[partner-attribution] store failed", e);
    }

    const resolved = await resolveSignupAttribution({
      userId: data.userId ?? null,
      email: data.email,
      affiliateSlug: data.affiliateSlug ?? null,
      grId: data.grId ?? null,
    });

    if (stored && resolved) {
      try {
        await supabaseAdmin
          .from("partner_signup_attributions")
          .update({
            resolved_partner_id: resolved.partnerId,
            resolved_referral_id: resolved.referralId,
            resolved_source: resolved.source,
            resolved_at: new Date().toISOString(),
          })
          .eq("user_id", data.userId ?? "");
      } catch {
        // logging only, never block signup
      }
    }

    return {
      ok: true,
      stored,
      attributed: resolved ? resolved.source : null,
    };
  });
